import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';

import { myProfile } from 'Redux/user-profile/selectors';
import { useConfig } from 'Context/ConfigContext';
import { useCapacitor } from 'Util/Capacitors';
import { OUTSIDE_LINK_URL, EXAM_MODE, QUIZ_VARIANT } from 'Util/constants';
import VideoEndingOverlayView from 'Components/VideoEndingOverlay';
import useMyExternalProfile from 'Routes/Classroom/Domain/useMyExternalProfile';
import { useClassroomNavigation } from 'Util/Hooks';
import { navigateToTungKai } from 'Util/navigateToTungKai';
import { canRedirectToTungKai } from 'Routes/MyCourses/Components/utils.js';

const VideoEndingOverlay = ({ onCloseOverlay, onReplayClick, courseId, user }) => {
  const { data } = useMyExternalProfile();
  const providerName = data?.myExternalProfile?.providerName ?? '';
  const { goToNextSubsection, isLastSubsectionInCourse, nextSubsection } = useClassroomNavigation();
  const { classroom = {} } = useConfig();
  const { enable: isEnableAutoPlay = false, leadTime = 7000 } =
    classroom.autoNextSubsectionOnVideoEnd ?? {};
  const [countdown, setCountdown] = useState(Math.floor(leadTime / 1000));
  const [enabledCountdown, setEnabledCountdown] = useState(isEnableAutoPlay);
  const [shouldShowOverlay, setShouldShowOverlay] = useState(true);
  const [timerID, setTimerID] = useState(null);

  const history = useHistory();
  const { openUrl, openInApp } = useCapacitor();
  const { tungKai, userApi } = useConfig();
  const examMode = nextSubsection?.metadata?.examMode;
  const isQuizFeed = nextSubsection?.contentType === 'QUIZ_FEED';
  const userId = user?.id;

  const handleAutoPlay = () => {
    const { enable, enableWhiteList, whitelist, quizFooterRedirectPath } = tungKai || {};
    const canGoToTungKai = canRedirectToTungKai({
      enable,
      enableWhiteList,
      whitelist,
      userId,
    });

    if (isLastSubsectionInCourse) {
      return history.push('/');
    } else if ((examMode === EXAM_MODE.RANDOM || isQuizFeed) && canGoToTungKai) {
      const variant = isQuizFeed ? QUIZ_VARIANT.QUIZ_FEED : QUIZ_VARIANT.QUIZ_V2;
      const redirectPath = (quizFooterRedirectPath || '/')
        .replace('{courseId}', courseId)
        .replace('{subsectionId}', nextSubsection?.id)
        .replace('{variant}', variant);

      return navigateToTungKai({
        redirectPath,
        config: { userApi, tungKai },
        openInApp,
      });
    } else {
      return goToNextSubsection();
    }
  };

  const disableCountdown = () => {
    setEnabledCountdown(false);
  };

  const handleOpenOnlineSolution = () => {
    disableCountdown();
    openUrl(OUTSIDE_LINK_URL.ONLINE_SOLUTION);
  };

  const handleClearTimer = () => {
    clearInterval(timerID);
    setTimerID(null);
  };

  useEffect(() => {
    if (isEnableAutoPlay) {
      setTimerID(
        setInterval(() => {
          setCountdown((prevCountdown) => prevCountdown - 1);
        }, 1000)
      );
    }

    const handleMessageEvent = (event) => {
      if (event?.data === 'MODAL_INTERRUPT') {
        disableCountdown();
      }
      if (event?.data === 'VIDEO_ENDING_OVERLAY_MODAL_INTERRUPT') {
        setShouldShowOverlay(false);
      }
      if (event?.data === 'VIDEO_ENDING_OVERLAY_MODAL_CONTINUE') {
        setShouldShowOverlay(true);
      }
    };

    window.addEventListener('message', handleMessageEvent);

    return () => {
      handleClearTimer();
      window.removeEventListener('message', handleMessageEvent);
    };
  }, []);

  useEffect(() => {
    if (isEnableAutoPlay) {
      if (countdown === 0) {
        handleClearTimer();
        handleAutoPlay();
      }
      if (!enabledCountdown) {
        handleClearTimer();
      }
    }
  }, [countdown, enabledCountdown]);

  // Bridge Space on ODAH ending modal → same LAMS-centralized replay as Replay button.
  // LAMS already handles Space when the player itself is focused (no modal).
  // TODO : delete this when migrate replay to player
  useEffect(() => {
    if (!shouldShowOverlay) return undefined;

    const isEditableTarget = (target) =>
      target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;

    const handleKeyDown = (event) => {
      if (event.code !== 'Space' && event.key !== ' ') return;
      if (isEditableTarget(event.target)) return;
      if (event.repeat) return;
      event.preventDefault();
      onReplayClick?.('keyboard');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [shouldShowOverlay, onReplayClick]);

  return (
    <VideoEndingOverlayView
      onCloseOverlay={onCloseOverlay}
      onReplayClick={onReplayClick}
      countdown={countdown}
      enabledCountdown={isEnableAutoPlay && enabledCountdown}
      onAutoPlay={handleAutoPlay}
      isLastSubsectionInCourse={isLastSubsectionInCourse}
      onClickVideoEndingOverlayContent={disableCountdown}
      onOpenOnlineSolution={handleOpenOnlineSolution}
      providerName={providerName}
      shouldShowOverlay={shouldShowOverlay}
    />
  );
};

VideoEndingOverlay.propTypes = {
  onCloseOverlay: PropTypes.func,
  onReplayClick: PropTypes.func,
  courseId: PropTypes.string,
};

export default connect((state) => ({
  user: myProfile(state),
}))(VideoEndingOverlay);
